import {
  CommunicationSizeComponent,
  ComponentIds,
  Entity,
  HierarchyUtils,
  ProductComponent,
  ProppingSystem,
  WorldTransformComponent
} from '@inter-ikea-core/core';
import {
  AccessoryAddedSystem,
  FrameWithWallPosition,
  HelpLinesSystem,
  HideFromAssemblyInstructionsComponent,
  HideFromVpcImagesComponent,
  NightLightsSystem,
  ProductType,
  ScreenshotComponent,
  AssemblyInstructionsComponent,
  sortByWallPosition,
  FadeAnimationSystem,
  HideOccludingObjectsSystem,
  getNormalizedDirectionFromEntities,
  InteriorComponent,
  OuterMeasurementsComponent,
  AssemblyInstructionMeasurementData,
  AssemblyInstructionEntityPoint,
  getEntitiesWithWallPosition,
  getEntitiesWithWallPositionByWall,
  getCardinalDirectionForWall
} from '@inter-ikea-storageone/range';
import {
  CardinalDirection,
  IAdvancedViewer,
  IVector2,
  IVector3,
  IViewer,
  Vector3
} from '@inter-ikea-betrakta/viewer';
import screenshotSettings from 'config/screenshotSettings';
import {
  AssemblyInstructionsForFrameEntity,
  prepareAssemblyInstructions,
  reset
} from 'ducks/assemblyInstructions';
import { getScreenshotLayout, hideMeasurements, showMeasurementsWhenGoingBack } from 'ducks/layout';
import { getMarketProducts } from 'ducks/products';
import useWriteDirection from 'hooks/useWriteDirection';
import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { compressImage } from 'utils/imageUtils';
import { useCore } from './core';
import { VPCImageType, VpcConfigurationImage } from '@inter-ikea-storageone/range';
import { setHelpLinesLabelsVisible, setHelpLinesVisible } from 'ducks/helpLines';
import { useAppDispatch } from 'ducks/hooks';
import usePrevious from 'hooks/usePrevious';
import { normalize, sub } from '@inter-ikea-kvadrat/kvadrat';
import { VectorXYZ } from '@inter-ikea-storageone/range/src/utils/vectorUtils';
import { computeScreenshotDirection } from './screenshot/computeScreenshotDirection';

interface HolePosition {
  x: number;
  y: number;
  z: number;
}

interface Hole {
  id: number;
  position: IVector3;
  holeNumber: number;
}

interface ImageAndPoints {
  image: string;
  points: Array<{ x: number; y: number }>;
}

export type CaptureImageForSingleFrameAssemblyInstructionsType = (
  frameId: number,
  holes: Array<Hole>,
  ipexViewer: IViewer & IAdvancedViewer
) => Promise<ImageAndPoints>;

interface ConfigImages {
  mainImage: string;
  noDoorsImage: string;
}

interface ScreenshotContextProps {
  images: string[];
  vpcImages: VpcConfigurationImage[];
  setImage: (image: string) => void;
  assemblyMainImageData: { image: string; points: IVector2[] }[];
  assemblyKitchenImageData: {
    title: string;
    image: string;
    measurementPointsData: AssemblyInstructionMeasurementData;
    entityPointsData: AssemblyInstructionEntityPoint[];
  }[];
  resetImages: () => void;
  captureVPCImages: (_?: any) => Promise<VpcConfigurationImage[]>;
  captureInteriorSuggestion: (frame: Entity) => Promise<string>;
  captureAssemblyInstructions: () => Promise<void>;
  captureKitchenAssemblyInstructions: () => Promise<void>;
}

const ScreenshotContext = React.createContext<ScreenshotContextProps>({
  images: [],
  vpcImages: [],
  assemblyMainImageData: [],
  assemblyKitchenImageData: [],
  resetImages: () => {},
  setImage: () => {},
  captureVPCImages: Promise.resolve,
  captureInteriorSuggestion: Promise.resolve,
  captureAssemblyInstructions: Promise.resolve,
  captureKitchenAssemblyInstructions: Promise.resolve
});

interface Props {
  children: React.ReactNode;
}

export const useScreenshot = () => useContext(ScreenshotContext);

/**
 * Provider component for managing screenshot functionality in the application.
 * Handles capturing, storing, and managing various types of screenshots including VPC images and assembly instructions.
 *
 * @component
 * @example
 * ```tsx
 * <ScreenshotProvider>
 *   <App />
 * </ScreenshotProvider>
 * ```
 *
 * @param {Props} props - Component props
 * @param {React.ReactNode} props.children - Child components to be wrapped by the provider
 *
 * @returns {JSX.Element} ScreenshotProvider component
 *
 * @context ScreenshotContext
 * @provides {Object} value - Context value object
 * @provides {string[]} value.images - Array of screenshot image data URLs
 * @provides {VpcConfigurationImage[]} value.vpcImages - Array of VPC configuration images
 * @provides {Object} value.assemblyMainImageData - Main assembly image data and points
 * @provides {Function} value.resetImages - Function to reset all images
 * @provides {Function} value.setImage - Function to set a single image
 * @provides {Function} value.captureVPCImages - Function to capture VPC configuration images
 * @provides {Function} value.captureInteriorSuggestion - Function to capture interior suggestion images
 * @provides {Function} value.captureAssemblyInstructions - Function to capture assembly instruction images
 *
 * @remarks
 * The provider manages several types of screenshots:
 * - VPC (Visual Product Configuration) images
 * - Assembly instruction images
 * - Interior suggestion images
 *
 * It handles:
 * - Camera positioning and settings
 * - Scene preparation (hiding/showing entities)
 * - Image capture and processing
 * - State management for captured images
 * - Integration with core API and systems
 *
 * @see {@link ScreenshotContext}
 * @see {@link useScreenshot}
 */
export const ScreenshotProvider = ({ children }: Props) => {
  const [images, setImages] = useState<string[]>([]);
  const [vpcImages, setVpcImages] = useState<VpcConfigurationImage[]>([]);
  const [assemblyMainImageData, setAssemblyMainImage] = useState<
    {
      image: string;
      points: IVector2[];
    }[]
  >([]);
  const [assemblyKitchenImageData, setAssemblyKitchenImageData] = useState<
    {
      title: string;
      image: string;
      measurementPointsData: AssemblyInstructionMeasurementData;
      entityPointsData: AssemblyInstructionEntityPoint[];
    }[]
  >([]);
  const coreApi = useCore();

  const helpLinesPreviouslyEnabled = usePrevious(
    coreApi.getSystem<HelpLinesSystem>('HelpLinesSystem')?.visible
  );

  const dispatch = useAppDispatch();
  const writeDirection = useWriteDirection();

  const layout = useSelector(getScreenshotLayout);
  const allProducts = useSelector(getMarketProducts);

  const resetImages = () => {
    setImages([]);
    setAssemblyMainImage([]);
    setAssemblyKitchenImageData([]);
    dispatch(reset());
  };

  const setImage = (image: string) => {
    setImages([image]);
  };

  const stageVisuals = (proppingVisible: boolean) => {
    coreApi.kvadratRoom?.toggleRoom(false);
    coreApi.getSystem<AccessoryAddedSystem>('AccessoryAddedSystem')?.removeClippingFromAllDrawers();
    coreApi.entities.unselect();

    const doors = coreApi.entities.getByComponent(ComponentIds.DOOR_FRONT_COMPONENT_ID);
    coreApi.entities.setAllVisible(doors, true, true);

    coreApi.getSystem<FadeAnimationSystem>('FadeAnimationSystem')?.disable();
    coreApi.getSystem<HideOccludingObjectsSystem>('HideOccludingObjectsSystem')?.disable();

    if (!layout.proppingVisible && proppingVisible) {
      coreApi.getSystem<ProppingSystem>('ProppingSystem')?.enable();
    } else if (!proppingVisible) {
      coreApi.getSystem<ProppingSystem>('ProppingSystem')?.disable();
    }

    if (helpLinesPreviouslyEnabled)
      coreApi.getSystem<HelpLinesSystem>('HelpLinesSystem')?.disable();

    dispatch(hideMeasurements());
    dispatch(setHelpLinesVisible(false));
    dispatch(setHelpLinesLabelsVisible(false));
  };

  const restoreVisuals = () => {
    const doors = coreApi.entities.getByComponent(ComponentIds.DOOR_FRONT_COMPONENT_ID);

    coreApi.entities.setAllVisible(doors, layout.doorsVisible, true);

    layout.proppingVisible
      ? coreApi.getSystem<ProppingSystem>('ProppingSystem')?.enable()
      : coreApi.getSystem<ProppingSystem>('ProppingSystem')?.disable();

    coreApi.getSystem<FadeAnimationSystem>('FadeAnimationSystem')?.enable();
    coreApi.getSystem<HideOccludingObjectsSystem>('HideOccludingObjectsSystem')?.enable();

    dispatch(showMeasurementsWhenGoingBack(true));

    if (helpLinesPreviouslyEnabled) coreApi.getSystem<HelpLinesSystem>('HelpLinesSystem')?.enable();

    coreApi.kvadratRoom?.toggleRoom(true);
  };

  /**
   * Takes a screenshot of the scene with specific camera settings and entity configurations.
   *
   * @param cameraSettings - Object containing camera size configuration
   * @param cameraSettings.size - Object containing x and y dimensions for the screenshot
   * @param cameraSettings.size.x - Width of the screenshot
   * @param cameraSettings.size.y - Height of the screenshot
   * @param objectsToHide - Array of entities to hide before taking screenshot (default: empty array)
   * @param screenshotEntityObjectIds - Array of object IDs to include in the screenshot
   * @param direction - Camera direction in the scene
   *
   * @returns Promise<string> - Returns a promise that resolves to the screenshot data
   *
   * @remarks
   * The function performs the following steps:
   * 1. Disables the kvadrat room visualization
   * 2. Finds all entities with Frame and Screenshot components
   * 3. Gets all related entities in the hierarchy
   * 4. Calculates a normalized direction vector from the entities transforms
   * 5. Hides specified objects
   * 6. Captures and returns the screenshot
   *
   * ```
   */
  const takeScreenshot = async (
    cameraSettings: {
      size: {
        x: number;
        y: number;
      };
    },
    objectsToHide: Entity[] = [],
    screenshotEntityObjectIds: number[] = [],
    direction: VectorXYZ
  ): Promise<string> => {
    coreApi.kvadratRoom?.toggleRoom(false);

    coreApi.entities.setAllVisible(objectsToHide, false, true);

    const result = await coreApi.camera?.captureScreenshot(
      cameraSettings,
      screenshotEntityObjectIds,
      direction
    );

    coreApi.entities.setAllVisible(objectsToHide, true, true);

    return result;
  };

  const createEmptyConfig = (): ConfigImages => {
    return {
      mainImage: '',
      noDoorsImage: ''
    };
  };

  const createVpcConfiguration = async (config: ConfigImages): Promise<VpcConfigurationImage[]> => {
    const images: string[] = [];
    const vpcImages: VpcConfigurationImage[] = [];

    if (config.mainImage?.length > 0) {
      const compressedMainImage = await compressImage(config.mainImage);
      images.push(compressedMainImage);
      vpcImages.push({ typeCode: VPCImageType.MAIN, image: compressedMainImage });
    }

    if (config.noDoorsImage?.length > 0) {
      const compressedNoDoorImage = await compressImage(config.noDoorsImage);
      images.push(compressedNoDoorImage);
      vpcImages.push({ typeCode: VPCImageType.OPEN_DESIGN, image: compressedNoDoorImage });
    }

    setImages(images);
    setVpcImages(vpcImages);
    return vpcImages;
  };

  /**
   * Captures and processes VPC images for the application.
   * This function manages the capture process differently for mobile and desktop platforms.
   *
   * The process includes:
   * 1. Resetting existing images
   * 2. Temporarily disabling night lights system
   * 3. Preparing stage visuals
   * 4. Capturing images of doors and walls
   * 5. Restoring previous visual settings
   *
   * @returns {Promise<VpcConfigurationImage[]>} A promise that resolves to an array of VPC configuration images
   * @throws {Error} May throw an error during image capture or processing
   *
   * @example
   * const vpcImages = await captureVPCImages();
   */
  const captureVPCImages = async (): Promise<VpcConfigurationImage[]> => {
    resetImages();

    // Setup initial state
    const nightLightsSystem = coreApi.getSystem<NightLightsSystem>('NightLightsSystem');
    const wasNightLightsEnabled = nightLightsSystem?.isEnabled;
    nightLightsSystem?.disable();
    stageVisuals(true);

    // Get all relevant entities
    const doors = getDoors();
    const objectsToHide = coreApi.entities.getByComponent(HideFromVpcImagesComponent.id);

    const imageConfig = createEmptyConfig();

    const screenshotEntities = coreApi.entities.getByComponents([
      OuterMeasurementsComponent.id,
      ScreenshotComponent.id
    ]);

    const screenshotEntitiesInHierarchy = screenshotEntities.flatMap(entity =>
      HierarchyUtils.getAssemblyEntities(entity)
    );

    // Make all frames and their descendants visible
    screenshotEntitiesInHierarchy.forEach(frame => {
      // Remove fade materials from frame and descendants
      const allEntities = [frame, ...HierarchyUtils.descendants(frame)];
      allEntities.forEach(entity => {
        // Force the viewer object to be visible
        const viewerObjectId = coreApi.entities.getObjectId(entity);
        if (viewerObjectId) {
          coreApi.ipexViewer.set_visible(viewerObjectId, true);
        }
      });
    });

    const screenshotDirection = computeScreenshotDirection({
      coreApi,
      screenshotEntities,
      screenshotEntitiesInHierarchy
    });

    // Get the object IDs of the screenshot entities in the hierarchy
    const screenshotEntityObjectIds = coreApi.entities.getObjectIds(screenshotEntitiesInHierarchy);

    const proppingSystem = coreApi.getSystem<ProppingSystem>('ProppingSystem');

    if (proppingSystem) {
      // Disable propping for main image
      proppingSystem.disable();
      imageConfig.mainImage = await takeScreenshot(
        screenshotSettings.default,
        objectsToHide,
        screenshotEntityObjectIds,
        screenshotDirection
      );

      // Enable propping for no-door image
      proppingSystem.enable();
      imageConfig.noDoorsImage = await takeScreenshot(
        screenshotSettings.default,
        [...doors, ...objectsToHide],
        screenshotEntityObjectIds,
        screenshotDirection
      );
    } else {
      imageConfig.mainImage = await takeScreenshot(
        screenshotSettings.default,
        objectsToHide,
        screenshotEntityObjectIds,
        screenshotDirection
      );

      // If there are doors, take a screenshot without them
      if (doors.length > 0) {
        imageConfig.noDoorsImage = await takeScreenshot(
          screenshotSettings.default,
          [...doors, ...objectsToHide],
          screenshotEntityObjectIds,
          screenshotDirection
        );
      }
    }

    if (wasNightLightsEnabled) {
      coreApi.getSystem<NightLightsSystem>('NightLightsSystem')?.enable();
    }

    const vpcImages = await createVpcConfiguration(imageConfig);
    restoreVisuals();
    return vpcImages;
  };

  // Helper functions
  const getDoors = () => {
    const hingeDoors = coreApi.entities.getByComponent(ComponentIds.DOOR_FRONT_COMPONENT_ID);
    const slidingDoors = coreApi.entities.getByProductTypes([
      ProductType.PAX_SLIDING_DOOR_REGULAR_ASM,
      ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM
    ]);
    return [...hingeDoors, ...slidingDoors];
  };

  const captureInteriorSuggestion = async (frame: Entity): Promise<string> => {
    coreApi.getSystem<FadeAnimationSystem>('FadeAnimationSystem')?.disable();
    coreApi.getSystem<HideOccludingObjectsSystem>('HideOccludingObjectsSystem')?.disable();
    const frameDescendants = HierarchyUtils.descendants(frame);
    frameDescendants.forEach(child => {
      child.removeComponent(HideFromAssemblyInstructionsComponent.id);
    });

    const frameObjectIds = coreApi.entities.getObjectIdsByEntity(frame);
    const relatedObjectIds = HierarchyUtils.descendants(frame).flatMap(d =>
      coreApi.entities.getRelatedObjectIds(d.id, true)
    );

    const holeObjectIds = HierarchyUtils.descendants(frame)
      .flatMap(d => coreApi.entities.getObjectIdsByEntity(d))
      .filter((id): id is number => id !== null);

    const defaultBgColor = coreApi.ipexViewer.get_background_color();

    coreApi.ipexViewer.set_background_color(1, 1, 1);

    const camera = coreApi.camera.createCamera();

    const size = frame.getComponent<CommunicationSizeComponent>(
      ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
    )?.size;

    if (!size) {
      throw new Error('No size for current frame, this must be something wrong with the entity');
    }

    const imageData = await coreApi.screenshotHelpers.create_screenshot_for_assembly_instructions(
      [...frameObjectIds, ...relatedObjectIds, ...holeObjectIds],
      normalize(sub(camera.target, camera.position)),
      [],
      size?.width,
      size?.height,
      {},
      0,
      false,
      camera
    );
    coreApi.ipexViewer.set_background_color(defaultBgColor.r, defaultBgColor.g, defaultBgColor.b);

    coreApi.getSystem<FadeAnimationSystem>('FadeAnimationSystem')?.enable();
    coreApi.getSystem<HideOccludingObjectsSystem>('HideOccludingObjectsSystem')?.enable();
    return imageData.image;
  };

  const captureAssemblyInstructions = async () => {
    stageVisuals(false);

    const framesWithAssemblyInstructions = coreApi.entities.getByComponent(
      AssemblyInstructionsComponent.id
    );
    const allFrames = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);

    // Make all frames and their descendants visible
    allFrames.forEach(frame => {
      // Remove fade materials from frame and descendants
      const allEntities = [frame, ...HierarchyUtils.descendants(frame)];
      allEntities.forEach(entity => {
        // Force the viewer object to be visible
        const viewerObjectId = coreApi.entities.getObjectId(entity);
        if (viewerObjectId) {
          coreApi.ipexViewer.set_visible(viewerObjectId, true);
        }
      });
    });

    const interiorObjects = framesWithAssemblyInstructions
      .flatMap(f => HierarchyUtils.descendants(f))
      .filter(e => e.hasComponent(InteriorComponent.id))
      .filter(e => !e.hasComponent(HideFromAssemblyInstructionsComponent.id))
      .filter(Boolean);

    const interiorsObjectIds = interiorObjects.flatMap(interior => {
      const objectIds = coreApi.entities.getObjectIdsByEntity(interior);
      return objectIds.filter(Boolean);
    });

    const frameWallList: FrameWithWallPosition[] = allFrames
      .map(frame => {
        const position = coreApi.entities.getWorldPosition(frame)!;
        return {
          frameObjectIds: coreApi.entities.getObjectIdsByEntity(frame),
          wallPosition: coreApi.entities.getEntityWall(frame),
          position: new Vector3(position.X / 1000, position.Y / 1000, position.Z / 1000),
          hasAssemblyInstructions: framesWithAssemblyInstructions.includes(frame)
        };
      })
      .filter(Boolean);

    const sortedFramesObjects = sortByWallPosition(frameWallList, writeDirection);

    const frameObjectIds = sortedFramesObjects.flatMap(f => f.frameObjectIds);
    const framePositions = sortedFramesObjects
      .filter(frame => frame.hasAssemblyInstructions)
      .flatMap(f => f.position);

    const camera = coreApi.camera.createCamera();
    const direction = getNormalizedDirectionFromEntities(framesWithAssemblyInstructions);

    const imageData = await coreApi.screenshotHelpers.create_screenshot_for_assembly_instructions(
      [...frameObjectIds, ...interiorsObjectIds],
      direction,
      framePositions,
      screenshotSettings.default.size.x,
      screenshotSettings.default.size.y,
      {},
      0.03,
      false,
      camera
    );

    setAssemblyMainImage([{ image: imageData.image, points: imageData.points }]);

    camera.dispose();

    // Filter the products so we only get frames and assign the assembly instructions
    const filteredAssemblyInstructionsForFrames = framesWithAssemblyInstructions.map(
      (frame: Entity) => {
        const position = coreApi.entities.getWorldPosition(frame);
        const wallPosition = coreApi.entities.getEntityWall(frame);

        const productRef = frame.getComponent<ProductComponent>(ComponentIds.PRODUCT_COMPONENT_ID)
          ?.productRef;

        return {
          productRef,
          entityId: frame.id,
          position: { x: position.X, y: position.Y, z: position.Z },
          instructions: coreApi.entities.getAssemblyInstructionsForEntity(frame),
          wallPosition: wallPosition
        };
      }
    ) as AssemblyInstructionsForFrameEntity;

    restoreVisuals();

    dispatch(
      prepareAssemblyInstructions(
        allProducts,
        filteredAssemblyInstructionsForFrames,
        coreApi,
        captureImageForSingleFrameAssemblyInstructions,
        restoreVisuals,
        writeDirection
      )
    );
  };

  /**
   * Captures assembly instructions for kitchen frames.
   */
  const captureKitchenAssemblyInstructions = async () => {
    stageVisuals(false);

    const entitiesWithAssemblyInstructions = coreApi.entities.getByComponent(
      AssemblyInstructionsComponent.id
    );

    // Make all entities and their descendants visible
    entitiesWithAssemblyInstructions.forEach(entity => {
      const allEntities = [entity, ...HierarchyUtils.descendants(entity)];
      allEntities.forEach(entity => {
        const viewerObjectId = coreApi.entities.getObjectId(entity);
        if (viewerObjectId) {
          coreApi.ipexViewer.set_visible(viewerObjectId, true);
        }
      });
    });

    const entitiesWithWallPosition = getEntitiesWithWallPosition(entitiesWithAssemblyInstructions);
    const mapByWall = getEntitiesWithWallPositionByWall(entitiesWithWallPosition);
    const multiwall = Object.keys(mapByWall).length > 1;
    const entityIdMap = entitiesWithAssemblyInstructions.map((entity: Entity, index: number) => ({
      entity: entity,
      instructionNbr: index + 1
    }));

    // Get one image from the top with all the entities
    const topImage = await coreApi.screenshotHelpers.take_edge_sketch_screenshot(
      entitiesWithWallPosition,
      entityIdMap,
      CardinalDirection.TOP,
      screenshotSettings.default.size.x,
      screenshotSettings.default.size.y,
      multiwall
    );

    const wallImages = [];
    // Get one image for each wall with the entities on that wall
    for (const wallId of Object.keys(mapByWall)) {
      const entitiesOnWall = mapByWall[wallId].entitiesWithPosition;
      const direction = getCardinalDirectionForWall(mapByWall[wallId].wallEntity);

      const image = await coreApi.screenshotHelpers.take_edge_sketch_screenshot(
        entitiesOnWall,
        entityIdMap,
        direction,
        screenshotSettings.default.size.x,
        screenshotSettings.default.size.y,
        multiwall
      );
      wallImages.push(image);
    }
    setAssemblyKitchenImageData([topImage, ...wallImages]);

    restoreVisuals();
  };

  const captureImageForSingleFrameAssemblyInstructions: CaptureImageForSingleFrameAssemblyInstructionsType = async (
    frameId,
    holes,
    ipexViewer
  ) => {
    const frame = coreApi.entities.find(frameId);
    if (!frame) {
      throw new Error('Frame not found');
    }

    // Get object IDs
    const frameObjectIds = coreApi.entities.getObjectIdsByEntity(frame);
    const holeObjectIds = getValidHoleObjectIds(holes);
    const holePositions = convertHolePositionsToMeters(holes);

    // Setup camera
    const camera = coreApi.camera.createCamera();
    const frameWorldDirection = getFrameWorldDirection(frameObjectIds[0], ipexViewer);

    // Position camera for frame
    await positionCameraForFrame(camera, frame, frameObjectIds);

    try {
      // Create screenshot
      const imageData = await captureAssemblyScreenshot(
        frameObjectIds,
        [...holeObjectIds],
        frameWorldDirection,
        holePositions,
        camera
      );

      return {
        image: imageData.image,
        points: imageData.points.filter(
          (p): p is { x: number; y: number } => p !== undefined && p !== null
        )
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to capture assembly screenshot:', error);
      throw error;
    } finally {
      camera.dispose();
    }
  };

  // Helper functions
  const getValidHoleObjectIds = (holes: Array<Hole>): number[] => {
    return holes
      .flatMap(hole => coreApi.entities.getObjectIdsByEntityId(hole.id))
      .filter((id): id is number => id !== null);
  };

  const convertHolePositionsToMeters = (holes: Array<Hole>): HolePosition[] => {
    const MILLIMETERS_TO_METERS = 0.001;
    return holes.map(hole => ({
      x: hole.position.x * MILLIMETERS_TO_METERS,
      y: hole.position.y * MILLIMETERS_TO_METERS,
      z: hole.position.z * MILLIMETERS_TO_METERS
    }));
  };

  const getFrameWorldDirection = (frameObjectId: number, ipexViewer: IViewer & IAdvancedViewer) => {
    return ipexViewer.get_world_direction_from_local_direction(frameObjectId, {
      x: 0,
      z: -1,
      y: 0
    });
  };

  const positionCameraForFrame = async (camera: any, frame: Entity, frameObjectIds: number[]) => {
    const frameDirection = frame.getComponent<WorldTransformComponent>(
      ComponentIds.WORLD_TRANSFORM_COMPONENT_ID
    )?.transform.forward;

    if (frameDirection) {
      await camera
        .frame_objects(frameObjectIds, false, 0, {
          x: frameDirection.X,
          y: frameDirection.Y,
          z: frameDirection.Z
        })
        .catch((e: any) => {
          coreApi.reportErrorToSentry(new Error(e), { frameObjectIds });
          // If framing objects fails, we will reset the camera
          coreApi.camera.resetCamera();
        });
    }
  };

  const captureAssemblyScreenshot = async (
    frameObjectIds: number[],
    holeObjectIds: number[],
    frameWorldDirection: any,
    holePositions: HolePosition[],
    camera: any
  ) => {
    return await coreApi.screenshotHelpers.create_screenshot_for_assembly_instructions(
      [...frameObjectIds, ...holeObjectIds],
      frameWorldDirection,
      holePositions,
      screenshotSettings.assemblySingleFrame.size.x,
      screenshotSettings.assemblySingleFrame.size.y,
      {},
      0.04,
      false,
      camera
    );
  };

  return (
    <ScreenshotContext.Provider
      value={{
        images,
        vpcImages,
        assemblyMainImageData,
        assemblyKitchenImageData,
        resetImages,
        setImage,
        captureVPCImages,
        captureInteriorSuggestion,
        captureAssemblyInstructions,
        captureKitchenAssemblyInstructions
      }}
    >
      {children}
    </ScreenshotContext.Provider>
  );
};
